import React, { useEffect, useState } from 'react'

import { ProductTypeConfigValueItem } from 'constants/Constants'
import ColumnLabel from 'components/headers/table/ColumnLabel'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faEllipsisVertical, faPencil, faPlus, faTrash } from '@fortawesome/free-solid-svg-icons'
import _ from 'lodash'
import { ConfirmDeleteModal } from 'components/modals/ConfirmDeleteModal'
import InputField from 'components/inputs/InputField'
import { FormInputError } from 'IndexProductScreen'
import { SearchBar } from 'components/inputs/SearchBar'
import { PageLoadSpinner } from 'components/spinners/PageLoadSpinner'
import { BaseModal } from 'components/modals/BaseModal'
import { useProductTypes } from 'hooks/useProductTypes'
import { toastError, toastSuccess } from 'services/Toast'

const ConfigTableHeaders = () => {

    return (
        <div className="columns is-vcentered" style={{ borderBottom: '1px solid rgb(228, 228, 228)', backgroundColor: '#fafafa' }}>
            <div className="column is-3">   
                <ColumnLabel label={`Label`} />
            </div>
            <div className="column is-2">
                <ColumnLabel label={`Product Group Code (SKU)`} />
            </div>
            <div className="column is-2">
                <ColumnLabel label={`Weight (KG)`} />
            </div>
            <div className="column is-2">
                <ColumnLabel label={`External Back Height (mm)`} />
            </div>
            <div className="column is-2">
                <ColumnLabel label={`Internal Back Width (mm)`} />
            </div>
            <div className="column is-1">
                {/* Leave space for actions button. */}
            </div>
        </div>
    )
}

type ConfigItemFormState = {
    label: string,
    value: string,
    shortDescription: string,
    weight: string,
    weightMode: 'CONSTANT' | 'VARIABLE',
    legacyCode: string,
    externalBackHeight: string,
    internalBackWidth: string,
    isDeleted?: boolean,
}

const initialFormState: ConfigItemFormState = {
    label: '',
    value: '',
    shortDescription: '',
    weight: '',
    weightMode: 'CONSTANT',
    legacyCode: '',
    externalBackHeight: '',
    internalBackWidth: '',
}

const toFormState = (item: ProductTypeConfigValueItem): ConfigItemFormState => ({
    label: item.label,
    value: item.value,
    legacyCode: item.legacyCode,
    shortDescription: item.shortDescription ?? '',
    weight: item.weight?.value?.toString() ?? '',
    weightMode: item.weightMode,
    externalBackHeight: item.externalBackHeight?.value?.toString() ?? '',
    internalBackWidth: item.internalBackWidth?.value?.toString() ?? '',
    isDeleted: item.isDeleted
})

const toProductType = (form: ConfigItemFormState): ProductTypeConfigValueItem => ({
    label: form.label,
    value: form.value,
    legacyCode: form.legacyCode || form.label,
    shortDescription: form.shortDescription,
    weightMode: form.weightMode,
    ...(form.weightMode === 'CONSTANT' && {
        weight: { value: Number(form.weight), unitOfMeasurement: 'kg' },
    }),
    externalBackHeight: { value: Number(form.externalBackHeight), unitOfMeasurement: 'mm' },
    internalBackWidth: { value: Number(form.internalBackWidth), unitOfMeasurement: 'mm' },
    isDeleted: form.isDeleted ?? false
})

const isBlank = (value: unknown): boolean =>
    value === null || value === undefined || value === ''

const isNotPositiveNumber = (value: unknown): boolean =>
    isBlank(value) || !Number.isFinite(Number(value)) || Number(value) <= 0

const initialFormInputErrorState: FormInputError = {
    isError: false,
    errorMessage: null
}

interface FormErrorState {
    label: FormInputError,
    value: FormInputError,
    shortDescription: FormInputError,
    weight: FormInputError,
    externalBackHeight: FormInputError,
    internalBackWidth: FormInputError
}

const initialFormErrorState: FormErrorState = {
    label: initialFormInputErrorState,
    value: initialFormInputErrorState,
    shortDescription: initialFormInputErrorState,
    weight: initialFormInputErrorState,
    externalBackHeight: initialFormInputErrorState,
    internalBackWidth: initialFormInputErrorState,
}

const ManageProductTypesScreen = ({

}:{


}) => {
    const { 
        productTypes, 
        addProductType, 
        editProductType, 
        deleteProductType,
        isAdding,
        isEditing,
        isDeleting,
    } = useProductTypes()

    const [itemToDelete, setItemToDelete] = useState<ProductTypeConfigValueItem>(null)
    const [isInEditState, setIsInEditState] = useState<boolean>(false)
    const [isConfirmDeleteModalActive, setIsConfirmDeleteModalActive] = useState<boolean>(false)
    const [filterableProductTypes, setFilterableProductTypes] = useState<ProductTypeConfigValueItem[]>(null)
    const [isProductTypeFormModalActive, setIsProductTypeFormModalActive] = useState<boolean>(false)
    const [configItemFormState, setConfigItemFormState] = useState<ConfigItemFormState>(initialFormState)
    const [formErrorState, setFormErrorState] = useState<FormErrorState>(initialFormErrorState)

    // Update filterable product types when productTypes changes
    useEffect(() => {
        if (productTypes) {
            const nonDeletedProductTypes = productTypes.filter(_ => !_.isDeleted)
            setFilterableProductTypes(nonDeletedProductTypes)
        }
    }, [productTypes])


    const onChangeInput = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {

        setConfigItemFormState({
            ...configItemFormState,
            [e.target.name]: e.target.value
        })
    }

    const onCancelConfirmDeleteModal = () => {
        setIsConfirmDeleteModalActive(false)
        setItemToDelete(null)
    }

    const onDeleteProductTypeItem = async (e: React.MouseEvent, itemToDelete: ProductTypeConfigValueItem) => {
        e.preventDefault(); 
        setItemToDelete(itemToDelete)
        setIsConfirmDeleteModalActive(true) 
    }

    const onEditProductTypeItem = async (e: React.MouseEvent, itemToEdit: ProductTypeConfigValueItem) => {
        e.preventDefault(); 

        setIsInEditState(true)

        setConfigItemFormState(toFormState(itemToEdit))
        setIsProductTypeFormModalActive(true)
    }
   
    const onChangeSearchQuery = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (productTypes) {
            const nonDeletedProductTypes = productTypes.filter(_ => !_.isDeleted)
            const lowercaseValue: string = e.target.value.toLowerCase()
            setFilterableProductTypes(nonDeletedProductTypes.filter(item => item.value.toLowerCase().startsWith(lowercaseValue) || item.label.toLowerCase().startsWith(lowercaseValue)))
        }
    }

    const onClearSearchQuery = (e: React.MouseEvent) => {
        if (productTypes) {
            const nonDeletedProductTypes = productTypes.filter(_ => !_.isDeleted)
            setFilterableProductTypes(nonDeletedProductTypes)
        }
    }

    const onCancelModal = (e: React.MouseEvent) => {
        e.preventDefault()
        setConfigItemFormState(initialFormState)
        setFormErrorState(initialFormErrorState)
        setIsProductTypeFormModalActive(false)
        setIsInEditState(false)
    }

    const deleteProductTypeItem = async () => {
        try {
            await deleteProductType(itemToDelete)
            toastSuccess(`Success, product type deleted.`)
            onCancelConfirmDeleteModal()
        } catch (error) {
            toastError(error instanceof Error ? error.message : undefined)
        }
    }

    const validateForm = (): boolean => {

        const mustNotBeEmptyError: string = `Must not be empty`
        const mustBeGreaterThanZeroError: string = `Must be greater than zero`

        const err = (isError: boolean, errorMessage: string): FormInputError =>
            ({ isError, errorMessage: isError ? errorMessage : null })

        const { label, weight, weightMode, externalBackHeight, internalBackWidth } = configItemFormState

        const formErrors: FormErrorState = {
            label: err(isBlank(label), mustNotBeEmptyError),
            value: initialFormInputErrorState,              // assigned server-side
            shortDescription: initialFormInputErrorState,   // optional
            weight: weightMode === 'CONSTANT'
                ? err(isNotPositiveNumber(weight), mustBeGreaterThanZeroError)
                : initialFormInputErrorState,
            externalBackHeight: err(isNotPositiveNumber(externalBackHeight), mustBeGreaterThanZeroError),
            internalBackWidth: err(isNotPositiveNumber(internalBackWidth), mustBeGreaterThanZeroError),
        }

        setFormErrorState(formErrors)

        return Object.values(formErrors).every(formError => !formError.isError)
    }

    const onSubmitForm = async (e: React.MouseEvent) => {
        e.preventDefault()

        const isFormValid: boolean = validateForm();

        if (isFormValid) {

            const productType: ProductTypeConfigValueItem = toProductType(configItemFormState)

            try {
                if (isInEditState) {
                    await editProductType(productType)
                    toastSuccess(`Success, product type updated.`)
                } else {
                    const response = await addProductType(productType)
                    toastSuccess(`Success, product type ${response.value} created.`)
                }
                onCancelModal(e)
            } catch (error) {
                toastError(error instanceof Error ? error.message : undefined)
            }
        }
    }

    return(
        <div>  
            <BaseModal
                isModalActive={isProductTypeFormModalActive}
                modalTitle={`${isInEditState ? `Edit `: `Add New `} Product Type`}
                onCancelModal={onCancelModal}>
                    <form>

                        {isInEditState && 
                            <InputField
                                fieldLabel={`Product Group Code (SKU)`}
                                onChange={onChangeInput}
                                fieldName={'value'}
                                value={configItemFormState.value}
                                errorValue={formErrorState.value}
                                helpText={`Product Group Code (SKU) assigned on add and cannot be changed`}
                                isDisabled={true} />
                        }

                        <InputField
                            fieldLabel={`Label`}
                            onChange={onChangeInput}
                            fieldName={'label'}
                            value={configItemFormState.label}
                            errorValue={formErrorState.label}
                            helpText={``} />

                        <div className="field pb-1">
                            <label className="label">Weight Mode</label> 
                            <input onChange={onChangeInput} className="is-checkradio" id="constantWeightMode" type="radio" value="CONSTANT" name="weightMode" checked={configItemFormState.weightMode === 'CONSTANT'} />
                            <label htmlFor="constantWeightMode">Constant</label>
                            <input onChange={onChangeInput} className="is-checkradio" id="variableWeightMode" type="radio" value="VARIABLE" name="weightMode" checked={configItemFormState.weightMode === 'VARIABLE'} />
                            <label htmlFor="variableWeightMode">Variable</label>
                        </div>

                        {configItemFormState.weightMode === 'CONSTANT' && 
                            <InputField
                                fieldLabel={`Weight`}
                                onChange={onChangeInput}
                                fieldName={'weight'}
                                value={configItemFormState.weight}
                                errorValue={formErrorState.weight}
                                hasAddOn={'kg'}
                                isNumber={true}
                                helpText={``} />
                        }
                            
                        <InputField
                            fieldLabel={`External Back Height`}
                            onChange={onChangeInput}
                            fieldName={'externalBackHeight'}
                            value={configItemFormState.externalBackHeight}
                            errorValue={formErrorState.externalBackHeight}
                            hasAddOn={'mm'}
                            isNumber={true}
                            helpText={``} />

                        <InputField
                            fieldLabel={`Internal Back Width`}
                            onChange={onChangeInput}
                            fieldName={'internalBackWidth'}
                            value={configItemFormState.internalBackWidth}
                            errorValue={formErrorState.internalBackWidth}
                            hasAddOn={'mm'}
                            isNumber={true}
                            helpText={``} />
                                                                    
                        <InputField
                            fieldLabel={`Short Description`}
                            onChange={onChangeInput}
                            fieldName={'shortDescription'}
                            value={configItemFormState.shortDescription}
                            errorValue={formErrorState.shortDescription}
                            isOptional={true}
                            helpText={``} />

                        <div className="field is-grouped is-pulled-right pt-4">
                            <div className="control">
                                <button className={`button is-link ${(isAdding || isEditing) ? `is-loading` : ``}`} onClick={onSubmitForm}>{isInEditState ? `Edit` : `Add`}</button>
                            </div>
                            <div className="control">
                                <button className="button is-link is-light" onClick={onCancelModal}>Cancel</button>
                            </div>
                        </div>

                    </form>
            </BaseModal>

        <ConfirmDeleteModal 
            isModalActive={isConfirmDeleteModalActive}
            isLoading={isDeleting}
            onCancel={onCancelConfirmDeleteModal}
            onConfirmDelete={deleteProductTypeItem} />

        <div className="columns mb-6">
            <div className="column is-10">
                    <h1 className="title is-3">Manage Product Types</h1>
                    <h1 className="subtitle is-5 mb-3"></h1>
            </div>
            <div className="column">
                <button className="button is-link is-pulled-right" onClick={() => setIsProductTypeFormModalActive(true)}>Add <FontAwesomeIcon className="pl-2" icon={faPlus} /></button>
            </div>
        </div>

        {productTypes && productTypes.length > 0 
        ?
        <>
        <div className="columns mb-4 pb-4">
            <div className="column is-5 p-0">
                <SearchBar 
                    onChange={onChangeSearchQuery}
                    onClear={onClearSearchQuery} />
            </div>
        </div>

        {filterableProductTypes && filterableProductTypes.length > 0
        ?
        <div className="columns">
            <div className="column is-12">
                <div className="block">

                    <ConfigTableHeaders />

                    {filterableProductTypes.map((item, idx) => (
                        <div className={`columns is-vcentered pb-1 pt-1 ${item.isDeleted ? `has-background-white-bis` : ``}`} key={`code-${idx}`} style={{borderBottom: '#e4e4e4 1px solid'}}>
                            <div className="column is-3">
                                {item.label}
                            </div>
                            <div className="column is-2">
                                {item.value}
                            </div>   
                            <div className="column is-2">
                                {item.weightMode === 'CONSTANT' 
                                ?
                                item.weight && `${item.weight.value}${item.weight.unitOfMeasurement}` || `--`
                                :
                                `-- variable --`
                                }
                            </div> 
                            <div className="column is-2">
                                {item.externalBackHeight && `${item.externalBackHeight.value}${item.externalBackHeight.unitOfMeasurement}` || `--`}
                            </div> 
                            <div className="column is-2">
                                {item.internalBackWidth && `${item.internalBackWidth.value}${item.internalBackWidth.unitOfMeasurement}` || `--`}
                            </div> 
                            {item.isDeleted
                                ?
                                <span className="tag is-danger is-light">Deleted</span>
                                :
                                <div className="column is-narrow">
                                <div className="dropdown is-hoverable is-pulled-right">
                                    <div className="dropdown-trigger pl-3 pr-3 is-clickable">
                                        <FontAwesomeIcon aria-haspopup="true" aria-controls="dropdown-menu4" icon={faEllipsisVertical} size={`lg`} />
                                    </div>
                                    <div className="dropdown-menu" id="dropdown-menu4" role="menu">
                                        <div className="dropdown-content">
                                            <a href="#" className="dropdown-item" onClick={(e) => onEditProductTypeItem(e, item)}>
                                                <span><FontAwesomeIcon icon={faPencil} className="pr-2" /> Edit</span>
                                            </a>
                                            <hr className="navbar-divider"/>
                                            <a href="#" className="dropdown-item" onClick={(e) => onDeleteProductTypeItem(e, item)}>
                                                <span><FontAwesomeIcon icon={faTrash} className="pr-2" /> Delete</span>
                                            </a>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            }       
                        </div>
                    ))}
                    
                </div>
            </div>
        </div>
        :
        <div className="has-text-centered">
            <p>No product type(s) found.</p>
        </div>
        }
        </>
        :
        <PageLoadSpinner />
    }         
    </div>
    )
}

export { ManageProductTypesScreen }